import { ConflictEvent } from "@ua/web-sdk/lib/contact/types"
import { PrivacyManagerFeature } from "@ua/web-sdk/lib/privacy-manager/types"
import { FeatureFlag } from "@ua/web-sdk/lib/remote-data/feature-flags"
import {
  PreferenceCenter,
  Scope,
} from "@ua/web-sdk/lib/remote-data/preference-center"
import {
  Platform,
  ScopedSubscriptionListIdentifier,
} from "@ua/web-sdk/lib/types"

export type Store = {
  debug: Debug
  metadata: Metadata
  contact: ContactData
  channel: ChannelData
  analytics: AnalyticsData
}

export type ChannelInfo = {
  id?: string
  optedIn: boolean
}

export type AsyncStatus = "idle" | "pending" | "fulfilled" | "rejected"

export type AsyncOperationStore = {
  status: AsyncStatus
  error?: string
  lastUpdated?: number
}

export type Metadata = {
  preferenceCenters?: PreferenceCenter[]
  localeOverride?: LocaleOverride
  conflictEvents?: ConflictEventWrapper[]
  supportInfo?: SupportInfo
  featureFlags?: FeatureFlagEligibilityCheck[]
} & AsyncOperationStore

export type FeatureFlagEligibilityCheck = {
  name: string
  timestamp: number
  flag: FeatureFlag
}

export type SupportInfo = {
  isSupported: boolean
  canRegister: boolean
  dataCollectionEnabled: boolean
}

export type LocaleOverride = {
  language?: string
  country?: string
}

export type ConflictEventWrapper = {
  occurred: number
  event: ConflictEvent
}

export type PreferenceCenterMetadata = {
  id: string
  name?: string
  type?: "contact" | "channel"
}

export type PushStatus = "unregistered" | "opted_in" | "opted_out"

export type DebugInfo = {
  channelId?: string
  pushStatus: PushStatus
  supported: boolean
  notificationPermission: PermissionState | "unsupported"
  contactId: string
  contactIsAnonymous: boolean
  pushHistory: Record<string, unknown>[]
  iaaDisplayPausedState: boolean
  privacyManagerFeatures: PrivacyManagerFeature[]
}

export type Debug = {
  debugInfo?: DebugInfo
} & AsyncOperationStore

export type ObjectDataStore = {
  tags: Tag[]
  attributes: { [key: string]: Attribute }
}

type AssociatedChannelBase = {
  platform: Platform
}

export interface AssociatedEmailChannel extends AssociatedChannelBase {
  platform: "email"
  address: string
}

export interface AssociatedOpenChannel extends AssociatedChannelBase {
  platform: "open"
  openPlatformName: string
  address: string
}

export interface AssociatedSmsChannel extends AssociatedChannelBase {
  platform: "sms"
  senderId: string
  msisdn: string
}

export type AssociatedChannel =
  | AssociatedEmailChannel
  | AssociatedOpenChannel
  | AssociatedSmsChannel

export type ContactData = {
  name?: string
  associated: AssociatedChannel[]
  subscription_lists: ScopedSubscriptionListIdentifier
} & AsyncOperationStore &
  ObjectDataStore

export type ChannelData = {
  id?: string
  optIn: boolean
  subscriptions: string[]
} & AsyncOperationStore &
  ObjectDataStore

export type AnalyticsData = {
  associatedIdentifiers: AssociatedIdentifier[]
} & AsyncOperationStore

export function isRequiredChannelData(
  data: ChannelData
): data is Required<ChannelData> {
  return !!data.id
}

export type Tag = {
  id: string
  tag: string
  group: string
}

export type Tags = {
  id: string
  tags: []
  group: string
}

export type SubscriptionListPayload = { listId: string; scope: Scope }

export const attributeTypes = ["number", "text", "date"] as const

export const AttributeTypeNames: { [K in AttributeType]: string } = {
  date: "Date",
  number: "Number",
  text: "Text",
}

export type AttributeType = typeof attributeTypes[number]

type AttributeBase = {
  id: string
  key: string
  type: AttributeType
}

export interface NumberAttribute extends AttributeBase {
  type: "number"
  value: number
}

export function isNumberAttribute(val: Attribute): val is NumberAttribute {
  return val.type === "number"
}

export interface TextAttribute extends AttributeBase {
  type: "text"
  value: string
}

export function isTextAttribute(val: Attribute): val is TextAttribute {
  return val.type === "text"
}

export interface DateAttribute extends AttributeBase {
  type: "date"
  value: string
}

export function isDateAttribute(val: Attribute): val is DateAttribute {
  return val.type === "date"
}

export type Attribute = NumberAttribute | TextAttribute | DateAttribute

export type AssociatedIdentifierMap = Map<string, string>
export type AssociatedIdentifier = Record<string, string>
export type AssociatedIdentifierObject = { key: string; identifier: string }

export const initialState: Store = {
  debug: { status: "idle" },
  metadata: { status: "idle" },
  contact: {
    tags: [],
    attributes: {},
    associated: [],
    status: "idle",
    subscription_lists: {},
  },
  channel: {
    tags: [],
    attributes: {},
    subscriptions: [],
    status: "idle",
    optIn: false,
  },
  analytics: {
    associatedIdentifiers: [],
    status: "idle",
  },
}

export type CustomEvent = {
  name: string
  value?: number
  transactionId?: string
  interactionId?: string
  interactionType?: string
  properties: CustomEventProperty[]
}

export const customEventPropertyTypes = [
  "string",
  "number",
  "boolean",
  "json",
] as const

export type CustomEventPropertyType = typeof customEventPropertyTypes[number]

export const CustomEventPropertyTypeName: {
  [K in CustomEventPropertyType]: string
} = {
  string: "String",
  number: "Number",
  boolean: "Boolean",
  json: "JSON",
}

type CustomEventPropertyBase = {
  key: string
  type: CustomEventPropertyType
}

export interface CustomEventStringProperty extends CustomEventPropertyBase {
  type: "string"
  value: string
}

export interface CustomEventNumberProperty extends CustomEventPropertyBase {
  type: "number"
  value: number
}

export interface CustomEventBooleanProperty extends CustomEventPropertyBase {
  type: "boolean"
  value: boolean
}

export interface CustomEventJsonProperty extends CustomEventPropertyBase {
  type: "json"
  value: string
}

export type CustomEventProperty =
  | CustomEventStringProperty
  | CustomEventNumberProperty
  | CustomEventBooleanProperty
  | CustomEventJsonProperty

export type SubscriptionList = { id: string; scopes?: Scope[] }
